import React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ControllerRenderProps } from "react-hook-form";
import { dt } from "../../../themes/redesignTokens";

export interface FormSelectOption {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
}

export interface FormSelectProps {
  // react-hook-form mode
  field?: ControllerRenderProps<Record<string, unknown>, string>;
  // standalone mode
  value?: string | number;
  onChange?: (value: string | number) => void;
  // common
  options: FormSelectOption[];
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
}

/**
 * Styled MUI Select dropdown with dt tokens.
 *
 * Based on SR urgency Select pattern with styled MenuProps paper.
 * Supports both react-hook-form Controller and standalone mode.
 */
const FormSelect: React.FC<FormSelectProps> = ({
  field,
  value,
  onChange,
  options,
  placeholder,
  error,
  helperText,
  fullWidth = true,
  size = "small",
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const resolvedValue = field ? (field.value as string | number) : (value ?? "");

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const newValue = event.target.value;
    if (field) {
      field.onChange(newValue === "" ? undefined : newValue);
    } else {
      onChange?.(newValue);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} error={error}>
      <Select
        value={resolvedValue}
        onChange={handleChange}
        displayEmpty={!!placeholder}
        sx={{
          fontFamily: dt.font,
          fontSize: "14px",
          borderRadius: dt.radiusSm,
          backgroundColor: dt.inputBg,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: dt.cardBorder,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: dt.blue,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: dt.blue,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: dt.radiusSm,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            },
          },
        }}
        renderValue={(selected) => {
          if (!selected && selected !== 0) {
            return (
              <Box sx={{ color: dt.textMuted, fontFamily: dt.font }}>
                {placeholder}
              </Box>
            );
          }
          const found = options.find((o) => o.value === selected);
          if (!found) return String(selected);
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontFamily: dt.font,
              }}
            >
              {found.icon}
              {found.label}
            </Box>
          );
        }}
      >
        {placeholder && (
          <MenuItem value="" sx={{ fontFamily: dt.font, color: dt.textMuted }}>
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={String(option.value)}
            value={option.value}
            sx={{ fontFamily: dt.font }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {option.icon}
              {option.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText sx={{ fontFamily: dt.font }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FormSelect;
